/**
 * ActivityFeed — renders a chronologically-grouped, filterable stream of
 * project activity events. Designed to match the Activity.html mockup.
 *
 * Usage:
 *   <ActivityFeed items={feed} hasMore={cursor !== null} onLoadMore={loadMore} />
 */

import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Avatar } from "@/components/Avatar";
import { FilterChips } from "@/components/FilterChips";
import { Icon } from "@/components/Icon";
import type { IconName } from "@/components/Icon";

// ─── Types ───────────────────────────────────────────────────────────────────

export type ActivityItem = {
  id: string;
  actorName: string;
  actorAvatarUrl?: string | null;
  action: string;
  verbVariant:
    | "created"
    | "completed"
    | "moved"
    | "comment"
    | "file"
    | "member"
    | "task"
    | "edited";
  target: {
    kind: "task" | "project" | "file" | "member" | "comment";
    id: string;
    title: string;
    projectId?: string;
    projectColor?: string;
    quote?: string;
    extra?: string;
    /** For comment events, the underlying task to navigate to. */
    taskId?: string;
  };
  createdAt: number; // unix epoch seconds
  type: "task" | "comment" | "file" | "member";
};

export type ActivityFeedProps = {
  items: ActivityItem[];
  hasMore: boolean;
  onLoadMore: () => void;
};

// ─── Constants ───────────────────────────────────────────────────────────────

/**
 * Icon mapping per verb variant.
 * Note: "edit" is not in IconName — substituted with "settings" (nearest available glyph).
 */
const ICON_BY_VARIANT: Record<ActivityItem["verbVariant"], IconName> = {
  created: "plus",
  completed: "check",
  moved: "arrow",
  comment: "chat",
  file: "clip",
  member: "users",
  task: "board",
  edited: "settings", // "edit" not in IconName — substituted with "settings"
};

const FILTER_OPTIONS = [
  { value: "all", label: "All" },
  { value: "task", label: "Tasks" },
  { value: "comment", label: "Comments" },
  { value: "file", label: "Files" },
  { value: "member", label: "Members" },
] as const;

type FilterValue = "all" | "task" | "comment" | "file" | "member";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function dayLabel(ts: number): string {
  const d = new Date(ts * 1000);
  const today = new Date();
  const yesterday = new Date(today.getTime() - 86400000);
  if (d.toDateString() === today.toDateString()) return "Today";
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "long",
  });
}

type DayGroup = { label: string; items: ActivityItem[] };

function groupByDay(items: ActivityItem[]): DayGroup[] {
  const groups: DayGroup[] = [];
  for (const item of items) {
    const label = dayLabel(item.createdAt);
    const last = groups[groups.length - 1];
    if (last && last.label === label) {
      last.items.push(item);
    } else {
      groups.push({ label, items: [item] });
    }
  }
  return groups;
}

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * ActivityFeed renders grouped, filterable activity events.
 *
 * @param items     - Ordered list of activity events (caller's order is preserved).
 * @param hasMore   - Whether a "Load more" button should be shown.
 * @param onLoadMore - Callback invoked when the user clicks "Load more".
 */
export function ActivityFeed({
  items,
  hasMore,
  onLoadMore,
}: ActivityFeedProps) {
  const [filter, setFilter] = useState<FilterValue>("all");

  const filtered =
    filter === "all" ? items : items.filter((i) => i.type === filter);

  const groups = groupByDay(filtered);

  return (
    <div className="activity-feed">
      <FilterChips
        options={FILTER_OPTIONS}
        value={filter}
        onChange={setFilter}
        ariaLabel="Activity filter"
      />

      {groups.map((g) => (
        <section key={g.label} className="day-group">
          <div className="day-label">{g.label}</div>

          {g.items.map((it) => (
            <article key={it.id} className="a-row act">
              {/* Avatar + event-type badge (colour applied via .ev-${variant}) */}
              <div className="a-avatar ava-wrap">
                <Avatar id={it.actorName} name={it.actorName} size={32} />
                <span className={`ev-ico ev-${it.verbVariant}`}>
                  <Icon name={ICON_BY_VARIANT[it.verbVariant]} size={10} />
                </span>
              </div>

              {/* Body: actor + verb, then target chip + extra, then quote, then time */}
              <div className="a-body a-main">
                <p className="a-text">
                  <b>{it.actorName}</b> {it.action}
                </p>
                <div className="a-target-row">
                  <ActivityTargetLink target={it.target}>
                    {it.target.projectColor ? (
                      <span
                        className="proj-dot"
                        style={{ background: it.target.projectColor }}
                      />
                    ) : null}
                    {it.target.title}
                  </ActivityTargetLink>
                  {it.target.extra ? (
                    <span className="a-extra">{it.target.extra}</span>
                  ) : null}
                </div>
                {it.target.quote ? (
                  <blockquote className="a-quote">{it.target.quote}</blockquote>
                ) : null}
                <time className="a-time">
                  {new Date(it.createdAt * 1000)
                    .toLocaleTimeString([], {
                      hour: "numeric",
                      minute: "2-digit",
                    })
                    .toUpperCase()}
                </time>
              </div>
            </article>
          ))}
        </section>
      ))}

      {hasMore && (
        <div className="a-more">
          <button type="button" className="btn-ghost" onClick={onLoadMore}>
            Load more
          </button>
        </div>
      )}
    </div>
  );
}

// ─── ActivityTargetLink ──────────────────────────────────────────────────────
// Routes an activity item's target chip to the right destination.
//   task / comment → /tasks/$taskId   (comment targets the parent task id)
//   project        → /projects/$projectId
//   file / member  → no nav (graceful fallback to a plain span)
function ActivityTargetLink({
  target,
  children,
}: {
  target: ActivityItem["target"];
  children: React.ReactNode;
}) {
  // For comment events, target.id is the comment id; the navigable task lives
  // in target.taskId. For task events the two are equivalent.
  const taskId =
    target.kind === "task"
      ? target.id
      : target.kind === "comment"
        ? target.taskId
        : undefined;
  if (taskId) {
    return (
      <Link to="/tasks/$taskId" params={{ taskId }} className="a-target">
        {children}
      </Link>
    );
  }
  if (target.kind === "project") {
    return (
      <Link
        to="/projects/$projectId"
        params={{ projectId: target.id }}
        className="a-target"
      >
        {children}
      </Link>
    );
  }
  return <span className="a-target">{children}</span>;
}
